import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FilterPipe } from '../../../shared/pipes/filter.pipe';

interface CheckRecord {
  id: string;
  keyValue: string;
  date: Date;
  status: 'passed' | 'failed' | 'pending';
  checkedBy?: string;
  remarks?: string;
}

interface SelfCheckForm {
  id: string;
  name: string;                  // 表單名稱 (設備名稱)
  formNumber?: string;           // 表單編號 (OSH-03-01-02)
  types: string[];               // 檢查類別 (作業檢點、定期檢查)
  departments: string[];         // 適用部門
  checkPeriod: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'beforeOperation';  // 檢查週期
  keyField: string;              // 主要欄位
  regulation?: string;           // 法規名稱(條文)
  qrCodeUrl?: string;
  createdAt: Date;
  records?: CheckRecord[];
}

@Component({
  selector: 'app-self-check-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule,FilterPipe],
  templateUrl: './self-check-list.component.html',
  styleUrl: './self-check-list.component.scss'
})
export class SelfCheckListComponent implements OnInit {
  selfCheckForms: SelfCheckForm[] = [];
  availableTypes: string[] = ['安全檢查', '設備維護', '環境檢查', '品質檢查', '作業檢點', '定期檢查'];
  availableDepartments: string[] = ['生產部', '品管部', '工務部', '物流部', '行政部'];

  // 更新檢查週期選項
  checkPeriods = [
    { value: 'beforeOperation', text: '作業前' },
    { value: 'daily', text: '每日' },
    { value: 'weekly', text: '每週' },
    { value: 'monthly', text: '每月' },
    { value: 'quarterly', text: '每三個月' },
    { value: 'yearly', text: '每年' }
  ];

  newForm: SelfCheckForm = this.getEmptyForm();
  selectedForm: SelfCheckForm | null = null;
  showFormDialog = false;
  showRecordDialog = false;
  currentTag = '';
  searchText = '';

  // Sample records data
  checkRecords: Map<string, CheckRecord[]> = new Map();

  // For the record table display
  recordTableHeaders: string[] = [];
  recordTableData: any[] = [];

  ngOnInit(): void {
    this.loadForms();
    this.initializeSampleRecords();
  }

  loadForms(): void {
    // 使用圖片中的數據創建表單
    this.selfCheckForms = [
      {
        id: '1',
        name: '壓縮式垃圾車',
        formNumber: 'OSH-03-01-02',
        types: ['作業檢點'],
        departments: ['環保部'],
        checkPeriod: 'beforeOperation',
        keyField: '車輛編號',
        regulation: '安衛辦法(50)',
        createdAt: new Date()
      },
      {
        id: '2',
        name: '壓縮式垃圾車',
        formNumber: 'OSH-03-01-03',
        types: ['定期檢查'],
        departments: ['環保部'],
        checkPeriod: 'quarterly',
        keyField: '車輛編號',
        regulation: '安衛辦法(14)',
        createdAt: new Date()
      },
      {
        id: '3',
        name: '資源/廚餘回收車',
        formNumber: 'OSH-03-01-04',
        types: ['作業檢點'],
        departments: ['環保部'],
        checkPeriod: 'beforeOperation',
        keyField: '車輛編號',
        regulation: '安衛辦法(50)',
        createdAt: new Date()
      },
      {
        id: '4',
        name: '資源/廚餘回收車',
        formNumber: 'OSH-03-01-05',
        types: ['定期檢查'],
        departments: ['環保部'],
        checkPeriod: 'quarterly',
        keyField: '車輛編號',
        regulation: '安衛辦法(14)',
        createdAt: new Date()
      },
      {
        id: '5',
        name: '微波爐',
        formNumber: 'OHS-03-01-06',
        types: ['定期檢查'],
        departments: ['總務部'],
        checkPeriod: 'monthly',
        keyField: '設備編號',
        regulation: '安衛辦法(14)',
        createdAt: new Date()
      }
    ];
  }

  initializeSampleRecords(): void {
    // Sample records for vehicle daily checks
    const vehicleRecords: CheckRecord[] = [
      { id: '101', keyValue: 'KEE-2332', date: new Date(2025, 4, 1), status: 'passed' },
      { id: '102', keyValue: 'KEE-2332', date: new Date(2025, 4, 2), status: 'passed' },
      { id: '103', keyValue: 'KEE-2332', date: new Date(2025, 4, 3), status: 'pending' },
      { id: '104', keyValue: 'KEP-3855', date: new Date(2025, 4, 1), status: 'passed' },
      { id: '105', keyValue: 'KEP-3855', date: new Date(2025, 4, 2), status: 'passed' },
      { id: '106', keyValue: 'KEP-3855', date: new Date(2025, 4, 3), status: 'pending' },
      { id: '107', keyValue: 'KEE-2336', date: new Date(2025, 4, 1), status: 'failed' },
      { id: '108', keyValue: 'KEE-2336', date: new Date(2025, 4, 2), status: 'passed' },
      { id: '109', keyValue: 'KEE-2336', date: new Date(2025, 4, 3), status: 'pending' },
      { id: '110', keyValue: 'KEE-2333', date: new Date(2025, 4, 1), status: 'passed' },
      { id: '111', keyValue: 'KEE-2333', date: new Date(2025, 4, 2), status: 'passed' },
      { id: '112', keyValue: 'KEE-2333', date: new Date(2025, 4, 3), status: 'pending' },
    ];

    const environmentRecords: CheckRecord[] = [
      { id: '201', keyValue: 'A區', date: new Date(2025, 0, 1), status: 'passed' },
      { id: '202', keyValue: 'A區', date: new Date(2025, 3, 1), status: 'passed' },
      { id: '203', keyValue: 'B區', date: new Date(2025, 0, 1), status: 'failed' },
      { id: '204', keyValue: 'B區', date: new Date(2025, 3, 1), status: 'passed' },
      { id: '205', keyValue: 'C區', date: new Date(2025, 0, 1), status: 'passed' },
      { id: '206', keyValue: 'C區', date: new Date(2025, 3, 1), status: 'pending' },
    ];

    this.checkRecords.set('1', vehicleRecords);
    this.checkRecords.set('2', environmentRecords);
  }

  getEmptyForm(): SelfCheckForm {
    return {
      id: '',
      name: '',
      formNumber: '',
      types: [],
      departments: [],
      checkPeriod: 'daily',
      keyField: '',
      regulation: '',
      createdAt: new Date()
    };
  }

  openFormDialog(form?: SelfCheckForm): void {
    if (form) {
      this.selectedForm = {...form};
    } else {
      this.selectedForm = this.getEmptyForm();
    }
    this.showFormDialog = true;
  }

  closeFormDialog(): void {
    this.showFormDialog = false;
    this.selectedForm = null;
  }

  saveForm(): void {
    if (!this.selectedForm) return;

    if (this.selectedForm.id) {
      // Update existing form
      const index = this.selfCheckForms.findIndex(f => f.id === this.selectedForm!.id);
      if (index !== -1) {
        this.selfCheckForms[index] = {...this.selectedForm};
      }
    } else {
      // Add new form
      const newForm = {
        ...this.selectedForm,
        id: Date.now().toString(),
        createdAt: new Date()
      };
      this.selfCheckForms.push(newForm);
    }

    this.closeFormDialog();
  }

  deleteForm(id: string): void {
    this.selfCheckForms = this.selfCheckForms.filter(form => form.id !== id);
  }

  generateQrCode(form: SelfCheckForm): void {
    // TODO: Implement QR code generation
    form.qrCodeUrl = `https://example.com/check/${form.id}`;
    alert(`QR Code for "${form.name}" has been generated!`);
  }

  viewRecords(form: SelfCheckForm): void {
    this.selectedForm = form;
    this.prepareRecordTable(form);
    this.showRecordDialog = true;
  }

  prepareRecordTable(form: SelfCheckForm): void {
    // Get records for the selected form
    const records = this.checkRecords.get(form.id) || [];

    if (records.length === 0) {
      this.recordTableHeaders = [];
      this.recordTableData = [];
      return;
    }

    // Generate table headers based on check period
    const dateHeaders = this.generateDateHeaders(form.checkPeriod);
    this.recordTableHeaders = [`${form.keyField}\\日期`, ...dateHeaders];

    // Group records by key value
    const keyValues = [...new Set(records.map(r => r.keyValue))];
    this.recordTableData = keyValues.map(keyValue => {
      const rowData: any = { keyValue };

      // Find records for this key value and add to the row
      dateHeaders.forEach(date => {
        const record = records.find(r => {
          const recordDate = this.formatDate(r.date);
          return r.keyValue === keyValue && recordDate === date;
        });

        if (record) {
          rowData[date] = record.status;
        } else {
          rowData[date] = null;
        }
      });

      return rowData;
    });
  }

  generateDateHeaders(checkPeriod: string): string[] {
    const today = new Date();
    const headers: string[] = [];

    switch (checkPeriod) {
      case 'daily':
        // Show current month days (up to 31)
        for (let i = 1; i <= 31; i++) {
          headers.push(i.toString());
        }
        break;
      case 'weekly':
        // Show weeks of current year
        for (let i = 1; i <= 52; i++) {
          headers.push(`W${i}`);
        }
        break;
      case 'monthly':
        // Show months of current year
        const months = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
        headers.push(...months);
        break;
      case 'quarterly':
        // Show quarters of current and next year
        const currentYear = today.getFullYear();
        headers.push(`Q1-${currentYear}`, `Q2-${currentYear}`, `Q3-${currentYear}`, `Q4-${currentYear}`);
        headers.push(`Q1-${currentYear + 1}`, `Q2-${currentYear + 1}`, `Q3-${currentYear + 1}`, `Q4-${currentYear + 1}`);
        break;
      case 'yearly':
        // Show last 5 years and next 5 years
        const year = today.getFullYear();
        for (let i = year - 4; i <= year + 5; i++) {
          headers.push(i.toString());
        }
        break;
      case 'beforeOperation':
        // Show a single header for before operation
        headers.push('Before Operation');
        break;
    }

    return headers;
  }

  formatDate(date: Date): string {
    // Simple formatter to match date with headers
    return date.getDate().toString();
  }

  getStatusIcon(status: string | null): string {
    if (!status) return '';

    switch (status) {
      case 'passed':
        return 'V';
      case 'failed':
        return 'X';
      case 'pending':
        return '+';
      default:
        return '';
    }
  }

  getStatusClass(status: string | null): string {
    if (!status) return '';

    switch (status) {
      case 'passed':
        return 'status-passed';
      case 'failed':
        return 'status-failed';
      case 'pending':
        return 'status-pending';
      default:
        return '';
    }
  }

  closeRecordDialog(): void {
    this.showRecordDialog = false;
    this.selectedForm = null;
  }

  addTag(type: 'types' | 'departments'): void {
    if (!this.currentTag || !this.selectedForm) return;

    if (!this.selectedForm[type].includes(this.currentTag)) {
      this.selectedForm[type].push(this.currentTag);
    }
    this.currentTag = '';
  }

  removeTag(type: 'types' | 'departments', tag: string): void {
    if (!this.selectedForm) return;

    this.selectedForm[type] = this.selectedForm[type].filter(t => t !== tag);
  }

  // 使用已有的檢查週期文字轉換函數
  getCheckPeriodText(period: string): string {
    const foundPeriod = this.checkPeriods.find(p => p.value === period);
    return foundPeriod ? foundPeriod.text : period;
  }
}
